import React, { useState } from 'react';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import GradingCriteria from 'components/GradingCriteria';
import { routes } from 'constants/config';
import RatingOptions from 'constants/rating-options';
import request from 'utils/request_ajax';

import { Button } from 'ui';
import Tooltip from 'components/shared/Tooltip';

const DemonstrationToGradeSection = ({ progress = {}, markProgressGraded }) => {
  const [success, setSuccess] = useState(null);
  const [comment, setComment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [criteriaRatings, setCriteriaRating] = useState({});

  const { demonstration } = progress;
  const isSubmitValid = success !== null;

  const submit = () => {
    setSaving(true);

    request
      .putJSON(routes.DEMONSTRATION_PROGRESSES_GRADE(progress.id), {
        demonstration_grade: {
          success,
          comment,
          feedbackCriteriaDemonstrationGradesAttributes: map(criteriaRatings),
        },
      })
      .then(data => {
        setSaving(false);
        if (data.success) {
          pubsub.publish('grades:update_pending_count');
          markProgressGraded();
        } else {
          console.error(data);
        }
      })
      .catch(e => {
        console.error(e);
        setSaving(false);
        // eslint-disable-next-line no-alert
        alert(t('common.server_error'));
      });
  };

  const onFeedbackCriteriaChange = (
    _a,
    criterionId,
    rating,
    feedbackCriterionComment,
  ) => {
    setCriteriaRating({
      ...criteriaRatings,
      [criterionId]: {
        ...criteriaRatings[criterionId],
        criterionId,
        rating,
        comment: feedbackCriterionComment,
      },
    });
  };

  return (
    <div>
      <h3 className="ff-ss fz-16 c-gray-light fw-b tt-u">
        {t('demonstrations.progress.submission_title')}
      </h3>
      <div>
        <div className="d-f w-fill border border-gray-light bg-gray-lighter fz-16 fw-b p-12">
          <span className="f-1 ml-16">{t('demonstrations.prompt')}</span>
          <span className="f-2 ml-32">
            {t('demonstrations.progress.given_answer')}
          </span>
        </div>
        <div className="d-f w-fill border border-gray-light border-top-0 px-8 py-16">
          <div className="f-1 ml-16">
            <span>{demonstration.prompt}</span>
          </div>
          <div className="f-2 ml-32">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={demonstration.givenAnswer}
              className="u-link"
            >
              {demonstration.givenAnswer}
            </a>
          </div>
        </div>
      </div>
      <h3 className="ff-ss fz-16 c-gray-light fw-b tt-u">
        {t('demonstrations.progress.feedback_title')}
      </h3>
      <div>
        <div className="d-f w-fill border border-gray-light bg-gray-lighter fz-16 fw-b p-12">
          <span className="f-1 ml-16">
            {t('demonstrations.progress.note_to_graders')}
          </span>
          <div className="d-f f-2">
            <span className="f-2 ml-16">
              {t('demonstrations.progress.feedback_title')}
            </span>
            <span className="f-1 ml-32">
              {t('demonstrations.progress.success')}
            </span>
          </div>
        </div>
        <div className="d-f w-fill border border-gray-light border-top-0 px-8 py-16">
          <div className="f-1 ml-16">
            <span>{demonstration.noteToGraders}</span>
          </div>
          <div className="d-f f-2 ml-32">
            <div className="d-f fd-c f-2">
              {demonstration.feedbackCriteria && (
                <div className="d-f f-2 fb-auto jc-sb m-fd-c">
                  <GradingCriteria
                    criteriaRatings={demonstration.feedbackCriteria}
                    ratingOptions={RatingOptions}
                    answerId={null}
                    onChange={onFeedbackCriteriaChange}
                    showComments
                  />
                </div>
              )}
              <div className="d-f fd-c f-2 fb-auto jc-sb m-fd-c">
                <div className="f-2 fb-auto mb-8 mr-8">
                  {t('demonstrations.progress.feedback')}
                </div>
                <div className="f-3 w-fill fb-auto pr-16">
                  <textarea
                    className="Textarea bg-white p-8 bdrs-2 h-70 w-fill"
                    placeholder={t(
                      'demonstrations.progress.feedback_placeholder',
                    )}
                    onChange={e => setComment(e.target.value)}
                    value={comment || ''}
                  />
                </div>
              </div>
            </div>
            <div className="f-1 ml-32 d-f fd-c">
              <label htmlFor={`${progress.id}_success`}>
                <input
                  id={`${progress.id}_success`}
                  type="radio"
                  onChange={() => setSuccess(true)}
                  checked={!!success}
                />
                <span className="ml-8">
                  {t('demonstrations.progress.success_label')}
                </span>
              </label>
              <label htmlFor={`${progress.id}_failure`}>
                <input
                  id={`${progress.id}_failure`}
                  type="radio"
                  onChange={() => setSuccess(false)}
                  checked={success === false}
                />
                <span className="ml-8">
                  {t('demonstrations.progress.failure_label')}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className="d-f jc-e mt-16">
        <Tooltip
          text={!isSubmitValid && t('grades.demonstrations_disabled_submit')}
          align="end"
        >
          <Button
            type="button"
            customColor
            onClick={submit}
            loading={saving}
            disabled={!isSubmitValid}
          >
            {t('grades.submit')}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

DemonstrationToGradeSection.propTypes = {
  progress: PropTypes.shape({
    id: PropTypes.number,
    userName: PropTypes.string,
    lessonName: PropTypes.string,
    daysAwaitingGrade: PropTypes.number,
    demonstration: PropTypes.shape({
      id: PropTypes.number,
      prompt: PropTypes.string,
      givenAnswer: PropTypes.string,
      noteToGraders: PropTypes.string,
      feedbackCriteria: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          criterion_id: PropTypes.number,
          name: PropTypes.string,
          description: PropTypes.string,
          score: PropTypes.number,
        }),
      ),
    }),
  }),
  markProgressGraded: PropTypes.func,
};

export default DemonstrationToGradeSection;
